import _ from "lodash";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  calculateSwitchTransactionSaldo,
  getTransactionValueByType,
  portfolioTransactionAmount,
  transactionValueToEuro,
  transactionValueToPercentage,
} from "../../../../../utils";
import {calculateTotalAmount} from "../../../../TradeStep/components/PortfolioTrade/table-data";
import {TRANSACTION_INSTR, TRANSACTION_TYPE_VALUES} from "../../../../../constants";
import {QTY_DECIMALS} from "../../../../../../../utils/utils";
import {fetchAssetPrice} from "../utils";
import {
  computeTransactionValue,
  computeSwitchAmountEuro,
  computeSwitchPercentage,
} from "../../transactionCalculations";


/**
 * Hook that encapsulates all transaction field change handlers.
 *
 * @param {Object} params
 * @param {Object} params.portfolio - The current portfolio object
 * @param {Function} params.handleChange - Low-level mutation function (item, data, isUpdatePortfolio)
 * @param {Function} params.handlePortfolioChange - Triggers portfolio-level change propagation
 * @param {boolean} params.isVirtual - Whether this is a virtual portfolio
 * @param {boolean} params.isNewVirtual - Whether this is for new virtual portfolio creation
 * @param {Function} params.newVirtualRecalculateTransactions - Recalculate weights for new virtual portfolios
 * @param {string} params.customer_id - Customer identifier
 * @param {Object} params.resource - API resource for virtual portfolio operations
 */
const useTransactionHandlers = ({
  portfolio,
  handleChange,
  handlePortfolioChange,
  isVirtual,
  isNewVirtual,
  newVirtualRecalculateTransactions,
  activeKey,
  customer_id,
  resource,
}) => {
  const dispatch = useDispatch();

  const roundQty = (value) => _.floor(value, portfolio.maxQtyDecimals || QTY_DECIMALS);

  const handleTransactionValueChange = (item, value, switchOutItem=undefined, withEuro=true, isUpdatePortfolio=true) => {
    const result = computeTransactionValue(value, item, switchOutItem, withEuro, portfolio.maxQtyDecimals);

    if (isVirtual && isNewVirtual) {
      item.transaction_value = value;
      portfolio.portfolioValueTotal = portfolioTransactionAmount(portfolio, TRANSACTION_TYPE_VALUES.BUY);
      newVirtualRecalculateTransactions(portfolio);
      result.calculated.transaction_value_weight = item.calculated.transaction_value_weight;
    }

    handleChange(item, {
      ...result,
      errors: {...item.errors, transaction_value: null, transaction_value_euro: null}
    }, isUpdatePortfolio);
  };

  const handleTransactionTypeChange = (item, value) => {
    handleChange(item, {
      transaction_type: value,
      transaction_value: isVirtual ? getTransactionValueByType(item, value) : null,
      errors: {
        ...item.errors,
        transaction_value: null,
        transaction_type: null
      }
    });
  };

  const handleDepositDateChange = (item, value, action) => {
    let isSwitch = action === TRANSACTION_TYPE_VALUES.SWITCH;
    let fieldsToUpdate = {deposit_from_date: value, errors: {...item.errors, deposit_from_date: null}, loading: isVirtual}

    if(isSwitch && isVirtual && !_.isEmpty(item.buy)) {
      fieldsToUpdate.buy = item.buy.map(buyTransaction => {return {...buyTransaction, loading: true} })
    }

    handleChange(item, fieldsToUpdate);

    // for virtual portfolio when date is changed fetch price of asset at that date
    if(isVirtual && value instanceof moment && value.isValid()){

      fetchAssetPrice(customer_id, item, value, dispatch, handleChange, isSwitch, resource);

      // Clean all entered data for switch transactions (only virtual) if booking date changed
      if (isSwitch) {
        const getCleanedData = (t) => ({
          calculated: {},
          transaction_value: null,
          sell_all: false,
          errors: {
            ...t.errors,
            transaction_value: null,
            transaction_value_euro: null,
            transaction_value_percentage: null
          }
        });

        _.flatten([item, item.buy || []]).forEach((t) => handleChange(t, getCleanedData(t)))
      }

    }
  };

  const handleSellAllChanged = (item, value, sellAllAvailable, totalValue=null) => {
    // in case "sell all" selected but not available - use QTY
    if(value && !sellAllAvailable) item.transaction_type = TRANSACTION_INSTR.qty;

    const isAmount = item.transaction_type === TRANSACTION_INSTR.amount;

    const transactionValue = value ? (isAmount ? item.data.market_value : item.data.quantity) : totalValue;

    handleChange(item, {
      sell_all: value,
      transaction_value: transactionValue,
      calculated: {
        transaction_value_percentage: transactionValue && transactionValueToPercentage(transactionValue, item),
        transaction_value_euro: transactionValue && transactionValueToEuro(transactionValue, item)
      },
      errors: {...item.errors, transaction_value: null, sell_all: null, transaction_value_euro: null}
    });
  };

  const handleBookingDateChange = (item, value, populate) => {
    const items = populate ? (portfolio.transactions[activeKey] || []) : [item];
    items.forEach(i => {
      handleChange(i, {
        ...value,
        errors: {...i.errors, deposit_from_date: null, deposit_from_type: null}
      }, false);
    });
    handlePortfolioChange(portfolio, {transactions: {...portfolio.transactions}});
  };

  const handleSellAllQuantityChanged = (item, value, sellAllAvailable, totalValue=null) => {
    item.quantity = value && (sellAllAvailable ? item.data.quantity : item.quantity) || 0;
    if (value) totalValue = calculateTotalAmount(item, item.limit, item.quantity);

    handleChange(item, {
      sell_all: value,
      transaction_value: totalValue,
      calculated: {
        transaction_value_percentage: totalValue && transactionValueToPercentage(totalValue, item),
        transaction_value_euro: totalValue && transactionValueToEuro(totalValue, item)
      },
      errors: {...item.errors, transaction_value: null, sell_all: null, transaction_value_euro: null}
    });
  };

  const handleDiscountChange = (item, value) => {
    handleChange(item, {discount: value, errors: {...item.errors, discount: null}});
  };

  const handleOngoingFeeChange = (item, value) => {
    handleChange(item, {ongoing_fee: value, errors: {...item.errors, ongoing_fee: null}});
  };

  const handleEntryFeeChange = (item, value) => {
    handleChange(item, {entry_fee: value, errors: {...item.errors, entry_fee: null}});
  };

  const handleEinzeltitelSettingChange = (item, settings, populate) => {
    const items = populate ? (portfolio.transactions[activeKey] || []) : [item];
    items.forEach(i => {
      handleChange(i, settings, false);
    });
    handlePortfolioChange(portfolio, {transactions: {...portfolio.transactions}});
  }

  const handleLimitValueChange = (item, value, amount) => {
    handleChange(item, {limit: value, transaction_value: amount, errors: {...item.errors, limit: null}});
  };

  const handleQuantityChange = (item, value, action) => {
    let quantity = value
    if (quantity && _.get(item, 'data.quantity') && action === TRANSACTION_TYPE_VALUES.SELL) {
      quantity = value <= item.data.quantity ? value : item.data.quantity
    }

    const _amount = calculateTotalAmount(item, item.limit, quantity);

    handleChange(item, {quantity: quantity, transaction_value: _amount, errors: {...item.errors, quantity: null}});
  };

  const handleConfirmRiskModalSave = (item, value, setConfirmRiskModalState) => {
    handleChange(item, {
      risk_score_explanation: value,
      errors: {...item.errors, risk_score_explanation: null}});
    setConfirmRiskModalState({
      confirmRiskModalOpen: false,
      confirmRiskModalActiveAsset: undefined
    })
  }

  const handleDepotFeeChanged = (value) => {
    handlePortfolioChange(portfolio, {depot_fee: value});
  };

  const handleSwitchPercentageChanged = (item, value, switchOutItem=undefined) => {
    const result = computeSwitchPercentage(value, item, switchOutItem, portfolio.maxQtyDecimals);

    handleChange(item, {
      ...result,
      errors: {...item.errors, transaction_value: null, transaction_value_euro: null}
    });
  }

  const handleSwitchAmountEuroChanged = (item, value, switchOutItem=undefined, isUpdatePortfolio=true) => {
    const result = computeSwitchAmountEuro(value, item, switchOutItem, portfolio.maxQtyDecimals);

    handleChange(item, {
      ...result,
      errors: {...item.errors, transaction_value: null, transaction_value_euro: null}
    }, isUpdatePortfolio);
  }

  /**
   * In case saldo value != 0 - distribute saldo quantity between Switch In transactions.
   */
  const handleDistributeQuantitiesClick = (switchOutItem) => () => {

    const precision = portfolio.maxQtyDecimals || QTY_DECIMALS

    const saldo = _.round(calculateSwitchTransactionSaldo(switchOutItem, portfolio.maxQtyDecimals), precision);

    if (saldo == 0 || _.isEmpty(switchOutItem.buy || [])) {
      return
    }

    switchOutItem.buy.reduce((currentSaldoValue, transaction) => {

      // Continue in case all quantities were distributed
      if (currentSaldoValue == 0) {
        return currentSaldoValue
      }

      let transactionValue = _.round(
        (transaction.transaction_value || 0) + currentSaldoValue, precision)

      if (transactionValue < 0) {
        // If Transaction value is negative after distribution
        // (saldo is big negative number) recalculate saldo value
        // and continue distributing for next transaction
        transactionValue = 0
        currentSaldoValue = _.round( currentSaldoValue + transaction.transaction_value || 0, precision)
      } else {
        currentSaldoValue = 0
      }

      handleTransactionValueChange(transaction, transactionValue, switchOutItem)

      return currentSaldoValue

    }, saldo)

  }

  return {
    roundQty,
    handleTransactionValueChange,
    handleTransactionTypeChange,
    handleSellAllChanged,
    handleBookingDateChange,
    handleSellAllQuantityChanged,
    handleDiscountChange,
    handleOngoingFeeChange,
    handleEntryFeeChange,
    handleEinzeltitelSettingChange,
    handleLimitValueChange,
    handleQuantityChange,
    handleConfirmRiskModalSave,
    handleDepotFeeChanged,
    handleSwitchPercentageChanged,
    handleSwitchAmountEuroChanged,
    handleDistributeQuantitiesClick,
  };
};

export default useTransactionHandlers;
